// GoZeek webapplication

import React, { useState, useEffect, useCallback, useTransition, Suspense } from 'react';
import { validateToken, setupInactivityHandler } from '../tokenVerification';
import './Dashboard.css';
import '../../utils/utils.css';
import { useHeader } from '../../utils/HeaderContext';
import { useNavigate } from 'react-router-dom';

import Logo from '../../assets/Logo.svg';
import dashboardIcon from '../../assets/dashboard_icon.svg';
import logoutI from '../../assets/logout_icon.svg';
// import GeneralTranslations from '../translations/generalTranslations';

const DashboardDefaultPage = React.lazy(() => import('../dashboard/DashboardDefaultPage'));
const Analytics = React.lazy(() => import('../analytics/Analytics'));
const AnalyticsMuseums = React.lazy(() => import('../analytics/AnalyticsMuseums'));
const AnalyticsRating = React.lazy(() => import('../analytics/AnalyticsRating'));
const AnalyticsOverview = React.lazy(() => import('../analytics/AnalyticsOverview'));
const Museums = React.lazy(() => import('../museums/Museums'));
const MuseumTypes = React.lazy(() => import('../museums/MuseumTypes'));
const Assignments = React.lazy(() => import('../assignments/Assignments'));
const AssignmentCategories = React.lazy(() => import('../assignments/AssignmentCategories'));
const AssignmentTypes = React.lazy(() => import('../assignments/AssignmentTypes'));
const AgeGroups = React.lazy(() => import('../administration/AgeGroups'));
const CMSUsers = React.lazy(() => import('../administration/CMSUsers'));
const UserPermissions = React.lazy(() => import('../administration/Roles'));
const Uploads = React.lazy(() => import('../administration/UploadFiles'));
const GeneralTranslations = React.lazy(() => import('../translations/generalTranslations'));

const Dashboard = () => {
  const [activeComponent, setActiveComponent] = useState('home');
  const [loading, setLoading] = useState(false);
  const [, startTransition] = useTransition();
  const navigate = useNavigate();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [fullname, setFullname] = useState('');
  const { headerTitle } = useHeader();
  const [features, setFeatures] = useState([]);
  const [error, setError] = useState(null);
  const [iconUrls, setIconUrls] = useState({});
  const customOrder = ['analytics', 'museums', 'assignments', 'administration', 'others'];

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;

    if (user && user.fullname) {
      setFullname(user.fullname);
    } else {
      navigate('/');
    }

  }, [navigate]);

  useEffect(() => {
    const fetchFeatures = async () => {
      setLoading(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_GOZEEK_API_URL}/featuresDashboard`, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch features');
        }

        const data = await response.json();
        setFeatures(data.updatedFeatures);
      } catch (error) {
        console.error('Error fetching features:', error);
        setError('Error fetching features');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, []);


  useEffect(() => {
    const loadIcons = async () => {
      try {
        // Retrieve cached data from localStorage
        const cachedIcons = localStorage.getItem('iconsCache');

        if (cachedIcons && Object.keys(JSON.parse(cachedIcons)).length > 0) {
          setIconUrls(JSON.parse(cachedIcons));
        } else {
          // Function to convert image URLs to Base64, including error handling
          const convertToBase64 = async (url) => {
            let absoluteUrl = url;
            const apiUrl = process.env.REACT_APP_GOZEEK_API_URL || '';

            // Check if URL is absolute
            if (!/^https?:\/\//i.test(url)) {
              absoluteUrl = `${apiUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
            }

            try {
              const response = await fetch(absoluteUrl, {
                method: 'GET',
                credentials: 'include',
              });

              if (!response.ok) throw new Error(`Failed to fetch icon: ${response.statusText}`);

              const blob = await response.blob();
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.error(`Error fetching icon from "${absoluteUrl}":`, error);
              // Return default Logo if fetching fails
              const defaultResponse = await fetch(Logo, { credentials: 'include' });
              const defaultBlob = await defaultResponse.blob();
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(defaultBlob);
              });
            }
          };

          // Prepare cache promises for feature icons
          const featureIconPromises = features.map(async (feature) => {
            const iconUrl = await convertToBase64(feature.icon);
            return { featureName: feature.feature_name, iconUrl };
          });

          // Prepare cache promises for static icons
          // const staticIcons = [
          //   { name: 'Logo', src: Logo },
          //   { name: 'DashboardIcon', src: dashboardIcon },
          //   { name: 'LogoutIcon', src: logoutI }
          // ].map(async ({ name, src }) => {
          //   const iconUrl = await convertToBase64(src);
          //   return { featureName: name, iconUrl };
          // });

          // Combine both dynamic and static icons
          // const allIcons = await Promise.all([...featureIconPromises, ...staticIcons]);
          const allIcons = await Promise.all([...featureIconPromises]);

          const iconsMap = allIcons.reduce((acc, { featureName, iconUrl }) => {
            acc[featureName] = iconUrl;
            return acc;
          }, {});

          // Update state and save to cache
          setIconUrls(iconsMap);
          localStorage.setItem('iconsCache', JSON.stringify(iconsMap));
        }
      } catch (err) {
        console.error('Error during icon loading:', err.message);
      }
    };

    // Wait until features are set before attempting to load icons
    if (features.length > 0) {
      loadIcons();
    }
  }, [features]);

  // ==========================================================
  // handleLogout
  // ==========================================================
  const handleLogout = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_GOZEEK_API_URL;

      const response = await fetch(`${apiUrl}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Logout failed. Please try again.');
      }

      // Optionally clear the icon cache on logout
      localStorage.removeItem('iconsCache');

      // Clear user data from localStorage
      localStorage.removeItem('user');

      // Redirect to the login or home page
      navigate('/');

    } catch (err) {
      console.error('Error during logout:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ==========================================================
  // showSessionExpiredPopup
  // ==========================================================
  const showSessionExpiredPopup = useCallback(() => {
    // Example: Replace with a custom modal or toast if desired
    setError('Your session has expired. You will be logged out.');
  }, []);

  // ==========================================================
  // Validate token on mount & setup inactivity
  // ==========================================================
  useEffect(() => {
    // Validate token immediately
    validateToken(handleLogout, showSessionExpiredPopup);

    // Set up inactivity auto-logout
    const removeInactivityHandler = setupInactivityHandler(handleLogout, showSessionExpiredPopup);

    // Cleanup
    return () => {
      removeInactivityHandler();
    };
  }, [handleLogout, showSessionExpiredPopup]);

  const handleComponentClick = (componentName) => {
    if (activeComponent !== componentName) {
      setLoading(true);
      startTransition(() => {
        setActiveComponent(componentName);
        setLoading(false);
      });
    }
  };

  const renderMainContent = () => {
    const componentMap = {
      Analytics: <Analytics />,
      AnalyticsMuseums: <AnalyticsMuseums />,
      AnalyticsRating: <AnalyticsRating />,
      AnalyticsOverview: <AnalyticsOverview />,
      Museums: <Museums />,
      'Museum Types': <MuseumTypes />,
      Assignments: <Assignments />,
      'Assignment Types': <AssignmentTypes />,
      'Assignment Categories': <AssignmentCategories />,
      'Age Groups': <AgeGroups />,
      'CMS Users': <CMSUsers />,
      Roles: <UserPermissions />,
      Uploads: <Uploads />,
      GeneralTranslations: <GeneralTranslations />,
    };

    return componentMap[activeComponent] || <DashboardDefaultPage />;
  };

  const toggleSidebar = () => setIsSidebarExpanded((prev) => !prev);

  const handleLogoClick = () => {
    setActiveComponent('DashboardDefaultPage');
  };

  const LogoutButton = ({ handleLogout, iconSrc, altText, buttonText }) => {
    return (
      <button onClick={handleLogout} className="logout-button">
        <img src={iconSrc} alt={altText} className="logout-icon" crossOrigin="use-credentials" />
        <span className="logout-text">{buttonText}</span>
      </button>
    );
  };

  return (
    <div
      className={`dashboard-container ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}
      onMouseEnter={() => setIsSidebarHovered(true)}
      onMouseLeave={() => setIsSidebarHovered(false)}
    >
      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <img src={Logo} alt="Loading" className="loading-logo" crossOrigin="use-credentials" />
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Header */}
      <header className={`dashboard-header ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="header-left" >
          <div onClick={handleLogoClick}>
            <img src={Logo} alt="GoZeek Logo" className="header-icon" crossOrigin="use-credentials" />
          </div>
          <h1
            className={`page-title ${isSidebarExpanded ? '' : 'page-title-collapse'}`}
          >
            {headerTitle}
          </h1>

        </div>
        <div className="header-right">
          <span className="username">
            Welcome: {fullname}
          </span>
          <LogoutButton
            iconSrc={logoutI}
            handleLogout={handleLogout}
            altText="Logout Icon"
            buttonText="Log Out"
          />
        </div>

      </header >

      <aside className={`dashboard-sidebar ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-toggle-container">
          {isSidebarExpanded || isSidebarHovered ? (
            <img src={Logo} alt="Logo" className="sidebar-logo" onClick={handleLogoClick} crossOrigin="use-credentials" />
          ) : null}
          <button className="sidebar-toggle-button" onClick={toggleSidebar}>
            <img src={dashboardIcon} alt="Toggle Sidebar" className="sidebar-toggle-icon" crossOrigin="use-credentials" />
          </button>
        </div>
        <nav>
          {/* Sidebar Menu Items */}
          {features.reduce((acc, feature) => {
            // Check if the category already exists in the accumulator
            const existingCategory = acc.find(item => item.category === feature.feature_category);

            if (existingCategory) {
              // If the category exists, add the feature to its list
              existingCategory.features.push(feature);
            } else {
              // If the category doesn't exist, create a new entry for it
              acc.push({
                category: feature.feature_category,
                features: [feature],
              });
            }

            return acc;
          }, [])
            .sort((a, b) => customOrder.indexOf(a.category) - customOrder.indexOf(b.category))
            .map(({ category, features }) => (
              <ul key={category}>
                {/* Render the category label once per group */}
                <li className={`pin-title ${isSidebarExpanded ? '' : 'hidden'}`}>
                  {category}
                </li>
                {features.map((feature, index) => (
                  <li
                    key={`${category}-${feature.feature_name}-${index}`}
                    onClick={() => handleComponentClick(feature.feature_name)}
                    className="sidebar-item"
                  >
                    <img
                      src={iconUrls[feature.feature_name] || Logo}
                      alt={`${feature.feature_name} Icon`}
                      className="icon"
                    />
                    <span className={`sidebar-text ${isSidebarExpanded ? '' : 'hidden'}`}>
                      {feature.feature_name}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`dashboard-main ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <Suspense fallback={<div>Loading...</div>}>
          {renderMainContent()}
        </Suspense>
      </main>
    </div >
  );
};

export default Dashboard;
